<template>
  <div class="passdaten-section">
    <div class="passdaten-section__head">
      <span class="passdaten-section__titel">{{ kategorie.titel }}</span>
      <span v-if="kategorie.hinweis" class="passdaten-section__hinweis">{{ kategorie.hinweis }}</span>

      <template v-if="!kategorie.nurDom">
        <span class="passdaten-section__spalte passdaten-section__spalte--dom">DOM</span>
        <span class="passdaten-section__spalte passdaten-section__spalte--fom">FOM</span>
      </template>
    </div>

    <div
      v-for="feld in kategorie.felder"
      :key="feld.keyDom"
      class="passdaten-section__zeile"
      :class="{
        'passdaten-section__zeile--geaendert': istGeaendert(feld),
        'passdaten-section__zeile--control': istControl(feld),
      }"
    >
      <span class="passdaten-section__label">{{ feld.label }}</span>

      <!-- Checkbox-Felder belegen die DOM- und, sofern vorhanden, die FOM-Spalte -->
      <template v-if="feld.type === FELDTYP.CHECKBOX">
        <div class="passdaten-section__wert">
          <button
            type="button"
            class="passdaten-section__checkbox passdaten-section__checkbox--dom"
            :class="{ 'passdaten-section__checkbox--checked': form[feld.keyDom] }"
            :disabled="!domEditable"
            @click="toggle(feld.keyDom)"
          >
            <Check v-if="form[feld.keyDom]" :size="12" :stroke-width="3" />
          </button>
        </div>
        <div v-if="!kategorie.nurDom" class="passdaten-section__wert">
          <button
            v-if="feld.keyFom"
            type="button"
            class="passdaten-section__checkbox passdaten-section__checkbox--fom"
            :class="{ 'passdaten-section__checkbox--checked': form[feld.keyFom] }"
            :disabled="!fomEditable"
            @click="toggle(feld.keyFom)"
          >
            <Check v-if="form[feld.keyFom]" :size="12" :stroke-width="3" />
          </button>
          <span v-else class="passdaten-section__leer">–</span>
        </div>
      </template>

      <!-- Passart als Mehrfachauswahl, gesperrt solange kein Passdaten-Feld gesetzt ist -->
      <div
        v-else-if="feld.type === FELDTYP.MULTISELECT"
        class="passdaten-section__control"
        v-b-tooltip.hover="{ customClass: 'tooltip-opacity' }"
        :title="passartDisabled && feld.hinweis ? feld.hinweis : ''"
      >
        <div :class="{ 'passdaten-section__gesperrt': passartDisabled || !domEditable }">
          <AppSelect
            :label="feld.label"
            :placeholder="`${feld.label} wählen`"
            multiple
            hide-search
            :options="feld.options"
            :value="form[feld.keyDom] || []"
            @input="onMultiselect(feld.keyDom, $event)"
          />
        </div>
      </div>

      <!-- Dringlichkeit als Einfachauswahl -->
      <div v-else-if="feld.type === FELDTYP.SELECT" class="passdaten-section__control">
        <div :class="{ 'passdaten-section__gesperrt': !domEditable }">
          <AppSelect
            :label="feld.label"
            hide-search
            :placeholder="`${feld.label} wählen`"
            :options="feld.options"
            :value="form[feld.keyDom] || 0"
            @input="onSelect(feld.keyDom, $event)"
          />
        </div>
      </div>

      <div v-else-if="feld.type === FELDTYP.SEGMENTED" class="passdaten-section__control">
        <div :class="{ 'passdaten-section__gesperrt': !domEditable }">
          <RestriktionenSegmentedControl
            :options="feld.options"
            :value="form[feld.keyDom]"
            @input="$emit('input', { key: feld.keyDom, value: $event })"
          />
        </div>
      </div>

      <div v-else class="passdaten-section__control">
        <b-form-input
          class="passdaten-section__input"
          :value="form[feld.keyDom]"
          :disabled="!domEditable"
          @input="$emit('input', { key: feld.keyDom, value: $event })"
        ></b-form-input>
      </div>
    </div>
  </div>
</template>

<script>
import { Check } from 'lucide-vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import RestriktionenSegmentedControl from '@/components/restriktionen/restriktionen-segmented-control.vue';
import { FELDTYP, PASSDATEN_KEYS } from './passdaten-konfiguration.felder.js';

export default {
  name: 'PassdatenKonfigurationSection',
  components: { Check, AppSelect, RestriktionenSegmentedControl },
  props: {
    kategorie: { type: Object, required: true },
    form: { type: Object, required: true },
    // Geladener Stand, dient dem Markieren ungespeicherter Änderungen
    originalForm: { type: Object, default: () => ({}) },
    domEditable: { type: Boolean, default: false },
    fomEditable: { type: Boolean, default: false },
  },
  data() {
    return { FELDTYP };
  },
  computed: {
    // Das Passart-Dropdown ist erst bedienbar, wenn mindestens ein Passdaten-Feld gesetzt ist
    passartDisabled() {
      return !PASSDATEN_KEYS.some(key => this.form[key]);
    },
  },
  methods: {
    istControl(feld) {
      return feld.type !== FELDTYP.CHECKBOX;
    },
    istGeaendert(feld) {
      return this.werteAbweichend(feld.keyDom) || (feld.keyFom && this.werteAbweichend(feld.keyFom));
    },
    werteAbweichend(key) {
      const aktuell = this.form[key];
      const original = this.originalForm[key];
      if (Array.isArray(aktuell) || Array.isArray(original)) {
        return JSON.stringify([...(aktuell || [])].sort()) !== JSON.stringify([...(original || [])].sort());
      }
      return aktuell !== original;
    },
    // Wandelt gespeicherte Werte in die Options-Objekte des Multiselects
    toggle(key) {
      this.$emit('input', { key, value: !this.form[key] });
    },
    onMultiselect(key, werte) {
      this.$emit('input', { key, value: werte });
    },
    onSelect(key, wert) {
      this.$emit('input', { key, value: wert ?? 0 });
    },
  },
};
</script>

<style scoped>
.passdaten-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
}

.passdaten-section__head {
  display: grid;
  grid-template-columns: 1fr 96px 96px;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.passdaten-section__titel {
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: #181d1f;
}

.passdaten-section__hinweis {
  grid-column: 2 / -1;
  justify-self: end;
  color: #b5b5c3;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}

.passdaten-section__spalte {
  justify-self: center;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
}

.passdaten-section__spalte--dom {
  color: #1bc5bd;
}

.passdaten-section__spalte--fom {
  color: #ffa800;
}

.passdaten-section__zeile {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 96px 96px;
  align-items: center;
  padding: 10px 20px;
}

.passdaten-section__zeile + .passdaten-section__zeile {
  border-top: 1px solid #f3f4f7;
}

.passdaten-section__zeile:hover {
  background-color: #f9fafb;
}

.passdaten-section__zeile--control {
  grid-template-columns: minmax(0, 1fr) minmax(140px, 400px);
}

/* Marker für ungespeicherte Änderungen an dieser Zeile */
.passdaten-section__zeile--geaendert::before {
  content: '';
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background-color: #3699ff;
}

.passdaten-section__label {
  color: #464e5f;
  font-size: 13px;
  line-height: 18px;
}

.passdaten-section__wert {
  display: flex;
  justify-content: center;
}

.passdaten-section__control {
  justify-self: end;
  width: 100%;
  max-width: 420px;
}

.passdaten-section__control >>> .app-select {
  width: 100%;
}

.passdaten-section__control >>> .restriktionen-segmented__segment {
  padding: 0 12px;
}

.passdaten-section__checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #ffffff;
  color: #ffffff;
  cursor: pointer;
}

.passdaten-section__checkbox:disabled {
  cursor: default;
  opacity: 0.5;
}

.passdaten-section__checkbox--dom:not(:disabled):hover {
  border-color: #1bc5bd;
}

.passdaten-section__checkbox--fom:not(:disabled):hover {
  border-color: #ffa800;
}

.passdaten-section__checkbox--dom.passdaten-section__checkbox--checked {
  background: #1bc5bd;
  border-color: #1bc5bd;
}

.passdaten-section__checkbox--fom.passdaten-section__checkbox--checked {
  background: #ffa800;
  border-color: #ffa800;
}

.passdaten-section__leer {
  color: #b5b5c3;
  font-size: 13px;
}

.passdaten-section__gesperrt {
  pointer-events: none;
  opacity: 0.5;
}

.passdaten-section__input {
  border: 1px solid #e5eaee;
  border-radius: 8px;
  font-size: 13px;
}
</style>
